import React, { Fragment } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";

import { Profiles } from "../profiles/Profiles";
import { Button } from "../../components/button/Button";
import ProgressButton from "../../components/progressButton/ProgressButton";
import { resetProfiles } from "../../store/profiles/profiles.actions";
import { saveProfile } from "../../store/profiles/profile.actions";
import { Notificator } from "../../components/notification";
import {
  getDefaultProfileId,
  hasChanges,
} from "../../store/profiles/profiles.selectors";
import {
  showConfirmationDialog,
  withPromptDialog,
} from "../../hocs/withPrompt";
import { useIntlTranslate } from "../../hooks";
import { AriaPageTitle } from "../../components/ariaPageTitle";
import { useSelectedProfileName } from "./useSelectedProfileName";
import { withSettingsGuard } from "../../router/guards/withSettingsGuard";
import { useSelectedProfileId } from "./useSelectedProfileId";
import { SettingsContent } from "./SettingsContent";

const ProfilesSettingsComponent = withPromptDialog(
  ({ save, discard, hasChanges, profileId, ...profilesProps }) => {
    return (
      <Fragment>
        <Profiles {...profilesProps} selectedProfileId={profileId} />
        <div className="settings-page__footer">
          {hasChanges && (
            <div className="settings-page__unsaved-warning">
              <FormattedMessage id="unsaved_changes" />
            </div>
          )}
          <Button
            className="settings-page__action-btn"
            color={"secondary"}
            size={"large"}
            onClick={discard}
            disabled={!hasChanges}
          >
            <FormattedMessage id="discard" />
          </Button>
          <ProgressButton
            label={<FormattedMessage id="save" />}
            disabled={!hasChanges}
            className="settings-page__action-btn"
            onClick={() => save(profileId)}
          />
        </div>
      </Fragment>
    );
  }
);

ProfilesSettingsComponent.propTypes = {
  save: PropTypes.func.isRequired,
  discard: PropTypes.func.isRequired,
  hasChanges: PropTypes.bool,
  // TODO: following can be computed based on type of item in profiles prop
  //  clarify if it is really needed
  customProfilesAmount: PropTypes.number,
  limit: PropTypes.number.isRequired,
  addDialogWillShow: PropTypes.func,
};

const ProfilesSettings = ({ pageTitle = {}, profileId }) => {
  const dispatch = useDispatch();
  const translate = useIntlTranslate();
  const hasChanged = useSelector((state) => hasChanges(profileId, state));
  const limit = useSelector((state) => state.profiles.limit);
  const customProfilesAmount = useSelector(
    (state) => state.profiles.saved.keys.length - 3
  );

  const save = () =>
    dispatch(saveProfile(profileId)).catch((errors) => {
      const isServiceError =
        errors[0] &&
        errors[0].message !== "content_restriction_categories_error";

      const translationErrors = _.map(errors, ({ message }) => {
        return translate(message);
      });

      const notification = isServiceError
        ? Notificator.error("couldnt_save_changes_because", {
            values: {
              errorList: translationErrors.join(", "),
            },
          })
        : Notificator.error(errors[0].message);

      dispatch(notification);
      return Promise.reject();
    });
  const discard = () => dispatch(resetProfiles());
  const showAddDialogProps = hasChanged
    ? {
        addDialogWillShow: (nextAction) => {
          showConfirmationDialog({
            save,
            discard,
            nextAction,
          });
        },
      }
    : {};

  return (
    <Fragment>
      <AriaPageTitle {...pageTitle} />
      <ProfilesSettingsComponent
        {...showAddDialogProps}
        save={save}
        discard={discard}
        hasChanges={hasChanged}
        limit={limit}
        customProfilesAmount={customProfilesAmount}
        profileId={profileId}
      />
    </Fragment>
  );
};

export const MultipleProfileSettings = withSettingsGuard(() => {
  const selectedProfileName = useSelectedProfileName();
  const profileId = useSelectedProfileId();

  return (
    <ProfilesSettings
      profileId={profileId}
      pageTitle={{
        pageKey: "page_title_settings_protection_preferences_multiple",
        pageValues: { profileName: selectedProfileName },
      }}
    />
  );
});

export const SingleProfileSettings = () => {
  const profileId = useSelector(getDefaultProfileId);

  return (
    <SettingsContent>
      <ProfilesSettings
        profileId={profileId}
        pageTitle={{
          pageKey: "page_title_settings_protection_preferences_single",
        }}
      />
    </SettingsContent>
  );
};
